import Swiper from 'swiper';
import { Navigation, Pagination, FreeMode, Thumbs, Autoplay, Zoom, Grid, Mousewheel, A11y } from 'swiper/modules';
import safeJSONParse from '../utils/safeJsonParse';
import { BREAKPOINTS, SWIPER } from '../utils/constants';

const SWIPER_JSON_ERROR = 'Error in Swiper JSON';

// Standart Swiper init
const initSwiper = (slider = null) => {
  const swipers = slider ? [slider] : document.querySelectorAll('[data-swiper]');
  const thumbSwipers = [];
  const isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!swipers.length) return;

  window.theme.swiperObjects = [];

  swipers.forEach(swiper => {
    const slides = swiper.querySelectorAll(SWIPER.SLIDE_CLASS);
    let config = swiper.dataset.swiperConfig ? safeJSONParse(swiper.dataset.swiperConfig, false) : {};
    const swiperModules = [];
    !config ? console.error(SWIPER_JSON_ERROR) : null;
    if (config && isReducedMotion && 'autoplay' in config) {
      delete config.autoplay;
    }
    if (slides.length && config) {
      // Swiper operates by loading modules, that's why we check if a module is referenced in the configuration. If it is, we add it to the modules array.
      if ('navigation' in config) {
        swiperModules.push(Navigation);
      }
      if ('pagination' in config) {
        swiperModules.push(Pagination);
      }
      if ('freeMode' in config) {
        swiperModules.push(FreeMode);
      }
      if ('autoplay' in config) {
        swiperModules.push(Autoplay);
      }
      if ('mousewheel' in config) {
        swiperModules.push(Mousewheel);
      }
      if ('thumbs' in config) {
        swiperModules.push(Thumbs);
        thumbSwipers.forEach(thumbSwiper => {
          config = { ...config, ...{ thumbs: { swiper: thumbSwiper } } };
        });
      }
      if ('zoom' in config) {
        swiperModules.push(Zoom);
      }
      if ('a11y' in config) {
        swiperModules.push(A11y);
      }

      // Adding modules to the config
      config = { ...config, ...{ modules: swiperModules } };

      swiper.classList.add('swiper--active');
      const newSwiper = new Swiper(swiper, config);
      if (swiper.dataset.swiperThumbs) {
        thumbSwipers.push(newSwiper);
      }

      window.theme.swiperObjects.push(newSwiper);
    }
  });
};

// Init swiper for mobile and tablet only
const initSwiperMobile = (slider = null) => {
  const desktopMediaQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.DESKTOP}px)`);
  const swipers = slider ? [slider] : document.querySelectorAll('[data-swiper-mob]');

  if (!swipers.length) return;

  window.theme.swiperMobileObjects = [];

  const initSwiperOnMobile = () => {
    if (!desktopMediaQuery.matches) {
      swipers.forEach(swiper => {
        const slides = swiper.querySelectorAll(SWIPER.SLIDE_CLASS);
        let config = swiper.dataset.swiperConfig ? safeJSONParse(swiper.dataset.swiperConfig, false) : {};
        const swiperModules = [];
        !config ? console.error(SWIPER_JSON_ERROR) : null;
        if (slides.length && config) {
          if ('navigation' in config) {
            swiperModules.push(Navigation);
          }
          if ('pagination' in config) {
            swiperModules.push(Pagination);
          }
          if ('freeMode' in config) {
            swiperModules.push(FreeMode);
          }
          if ('thumbs' in config) {
            swiperModules.push(Thumbs);
          }
          if ('zoom' in config) {
            swiperModules.push(Zoom);
          }
          if ('grid' in config) {
            swiperModules.push(Grid);
          }
          if ('a11y' in config) {
            swiperModules.push(A11y);
          }

          config = { ...config, ...{ modules: swiperModules } };
          swiper.classList.add('swiper--mob-active');
          const newSwiper = new Swiper(swiper, config);
          window.theme.swiperMobileObjects.push(newSwiper);

        }
      });
    } else {
      if (window.theme.swiperMobileObjects) {
        window.theme.swiperMobileObjects.forEach(swiper => {
          swiper.destroy();
        });
        const keys = Object.keys(window.theme.swiperMobileObjects);
        keys.forEach(key => window.theme.swiperMobileObjects[key].destroy());
        window.theme.swiperMobileObjects = [];
      }
    }
  };
  if (desktopMediaQuery) {
    initSwiperOnMobile();
  }
  desktopMediaQuery.addEventListener('change', initSwiperOnMobile);
};

// Resolve the Swiper modules a config references (mirrors the checks above)
const getSwiperModules = (config) => {
  const moduleMap = {
    navigation: Navigation,
    pagination: Pagination,
    freeMode: FreeMode,
    autoplay: Autoplay,
    mousewheel: Mousewheel,
    thumbs: Thumbs,
    zoom: Zoom,
    grid: Grid,
    a11y: A11y
  };
  return Object.keys(moduleMap).filter(key => key in config).map(key => moduleMap[key]);
};

// Init swiper for desktop only — below the xl breakpoint the same markup is
// laid out by CSS (e.g. the header search recommended products grid)
const initSwiperDesktop = (slider = null) => {
  const desktopMediaQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.DESKTOP_LARGE}px)`);
  const swipers = slider ? [slider] : document.querySelectorAll('[data-swiper-desktop]');
  if (!swipers.length) return;

  window.theme.swiperDesktopObjects = [];

  const destroySwipers = () => {
    window.theme.swiperDesktopObjects.forEach(swiper => swiper.destroy());
    window.theme.swiperDesktopObjects = [];
  };

  const initSwiperOnDesktop = () => {
    if (!desktopMediaQuery.matches) {
      destroySwipers();
      return;
    }
    swipers.forEach(swiper => {
      if (swiper.swiper) return;
      const slides = swiper.querySelectorAll(SWIPER.SLIDE_CLASS);
      const config = swiper.dataset.swiperConfig ? safeJSONParse(swiper.dataset.swiperConfig, false) : {};
      if (!config) {
        console.error(SWIPER_JSON_ERROR);
        return;
      }
      if (!slides.length) return;
      const newSwiper = new Swiper(swiper, { ...config, modules: getSwiperModules(config) });
      window.theme.swiperDesktopObjects.push(newSwiper);
    });
  };

  initSwiperOnDesktop();
  desktopMediaQuery.addEventListener('change', initSwiperOnDesktop);
};

export { initSwiper, initSwiperMobile, initSwiperDesktop };
